/**
 * Every subsystem of the GUI, in one list: what it is, what it is built from, its commands.
 *
 * `services.ts` starts them with `shared/subsystems.ts`, which works out the order from the
 * `needs`. To add a service: write its `create…` function in its own file (`state/`,
 * `client/`...), add its field to `App` (`context.ts`), and one entry here. The list's order
 * is only for reading: client and mirrors first, then the editor, then the rest of the screen.
 */
import { createActions, graphCommands, graphMenus, graphToolbar } from "../client/actions";
import { createClient, type Transport } from "../client/client";
import type { SpaceLink } from "../client/space";
import { createGraphMirror } from "../client/graph";
import { createCommands } from "../shared/commands";
import type { DirectoryHandle } from "../shared/folders";
import { subsystemsOf, type Subsystem } from "../shared/subsystems";
import { createAccess } from "../state/access";
import { createEnvironments, environmentCommands, environmentMenus } from "../state/environments";
import { followEnvironmentLayouts } from "./environmentLayouts";
import { aiMenus, aiToolbar, createAi } from "../state/ai";
import { analysisMenus, analysisToolbar, createAnalysis } from "../state/analysis";
import { configMenus, createConfigMirror } from "../state/config";
import { createKinds } from "../state/kinds";
import { createFields } from "../state/fields";
import { createContextMenu } from "../state/menu";
import { createPlugins, pluginMenus } from "../state/plugins";
import { createPluginViews } from "../state/pluginViews";
import { createPresence } from "../state/presence";
import { createPresets, presetCommands, presetMenus, presetToolbar } from "../state/presets";
import { createAssets } from "../state/assets";
import { createDocuments } from "../state/documents";
import { createProject, projectCommands, projectMenus, projectToolbar } from "../state/project";
import { createDiagram } from "../state/diagram";
import { boardCommands, boardMenus, createBoard } from "../state/board";
import { createScene } from "../state/scene";
import { createSpaces, spacesCommands, spacesMenus } from "../state/spaces";
import { createShell } from "../state/shell";
import { createPalette, paletteCommands, paletteMenus } from "../state/palette";
import { createInspectorSections } from "../state/inspector";
import { DEFAULT_KEYBINDS } from "./keys";
import { createAccounts } from "../state/accounts";
import { createSceneSync } from "../state/sceneSync";
import { createView, viewCommands, viewMenus, viewToolbar } from "../state/view";
import { createNodeAdder, nodeAdderCommands, nodeAdderMenus, nodeAdderToolbar } from "../state/nodeAdder";
import { createPages } from "../state/pages";
import { createPlay } from "../state/play";
import { createPrototype, prototypeCommands, prototypeMenus } from "../state/prototype";
import { floatNear } from "./floatNear";
import type { Layout } from "../wm/layout";
import { createWindowManager, windowCommands, windowMenus, type WindowDef } from "../wm/manager";
import { layoutFromConfig } from "./configApply";
import type { App } from "./context";
import { createTheme, themeCommands, themeMenus } from "../state/theme";

/** What the page brings from outside: how to reach the core and what the platform can do. */
export interface Platform {
  /** How messages reach the core (WebSocket, in-page WASM core, or Tauri). */
  transport: Transport;
  /** The remote space this page works in, when it is not on its own core (`?remote=`). */
  remote: SpaceLink | null;
  /** The page's own space, when a server served it at `/s/<space>`. */
  page: SpaceLink | null;
  /** Whether a server served the page and it talks to that server's cores. */
  served: boolean;
  /** Whether the core reads folders on its own disk (not the standalone browser build). */
  readsFolders: boolean;
  /** Asks for a folder with the system's dialog (desktop app only). */
  pickFolder?: () => Promise<string | null>;
  /** The browser's folder picker, when the page reads folders itself. */
  directoryPicker?: () => Promise<DirectoryHandle>;
}

/**
 * The built-in windows and their default arrangement (`windows.ts`). Given from outside so this
 * list never loads the windows' components (and can be tested without a page).
 */
export interface BuiltInWindows {
  /** Every built-in window. */
  list: WindowDef[];
  /** Where they go when the config has no layout. */
  layout: () => Layout;
}

/** Everything the subsystems can need: the app's services, plus what comes from outside. */
export type Services = App & { platform: Platform; windows: BuiltInWindows };

/** Declares one subsystem of {@link Services} (checks its needs and its value's type). */
const define = subsystemsOf<Services>();

/** The GUI's subsystems. */
export const SUBSYSTEMS = [
  // Talking to the core.
  define({ id: "client", summary: "The connection to the core, requests by id, reconnects", needs: ["platform"], start: ({ platform }) => createClient(platform.transport), stop: (client) => client.close() }),
  define({ id: "graph", summary: "The core's graph, mirrored", needs: ["client"], start: ({ client }) => createGraphMirror(client) }),
  define({ id: "access", summary: "Who may do what here: your role, members, invites", needs: ["client"], start: ({ client }) => createAccess(client) }),
  define({ id: "spaces", summary: "Which board this page works on: a space on a server, or back", needs: ["platform"], start: ({ platform }) => createSpaces({ remote: platform.remote, page: platform.page, served: platform.served }), commands: (spaces) => spacesCommands(spaces), menus: (spaces) => spacesMenus(spaces) }),
  define({
    id: "accounts",
    summary: "Signed in to servers: teams, their spaces, members and invites",
    needs: ["platform"],
    start: ({ platform }) => createAccounts({ homeServer: platform.served ? location.origin : null }),
  }),
  define({ id: "actions", summary: "Every edit, sent to the core", needs: ["client", "graph"], start: ({ client, graph }) => createActions(client, () => void graph.resync(), () => ({ environment: graph.scope()?.id ?? null, workspace: graph.scope()?.workspace ?? null })), commands: (actions) => graphCommands(actions), menus: (_, { graph }) => graphMenus(graph), toolbar: (_, { graph }) => graphToolbar(graph) }),
  define({ id: "config", summary: "The core's configuration, mirrored", needs: ["client"], start: ({ client }) => createConfigMirror(client), menus: () => configMenus() }),
  define({ id: "project", summary: "The open project: name, file, saved or not", needs: ["client", "graph"], start: ({ client, graph }) => createProject(client, graph), commands: (project) => projectCommands(project), menus: (project) => projectMenus(project), toolbar: (project) => projectToolbar(project) }),

  // The editor.
  define({ id: "view", summary: "Selection, search, filters, link tool", needs: ["actions", "graph", "wm"], start: () => createView(), commands: (view, { actions, graph, wm }) => viewCommands(view, actions, graph, (id) => wm.open(id)), menus: (view) => viewMenus(view), toolbar: (view) => viewToolbar(view) }),
  define({ id: "scene", summary: "The graph as laid out on screen", needs: [], start: () => createScene() }),
  define({ id: "sceneSync", summary: "Keeps the scene in step with the graph, and one layout for everyone", needs: ["scene", "graph", "view", "project", "presence", "access"], start: (needs) => createSceneSync(needs) }),
  define({ id: "diagram", summary: "The class diagram: boxes in layers, its own view, boxes moved by hand", needs: ["graph", "view", "project", "actions", "presence", "fields"], start: ({ graph, view, project, actions, presence, fields }) => createDiagram({ graph, view, project, actions, presence, fields: fields.list }) }),
  define({
    id: "board",
    summary: "The board: items placed freely, frames, its own view, gestures shared live",
    needs: ["graph", "view", "project", "actions", "presence", "kinds", "environments"],
    start: ({ graph, view, project, actions, presence, kinds, environments }) => createBoard({ graph, view, project, actions, presence, kinds, prototypes: () => !!environments.current()?.windows.includes("prototype") }),
    commands: (board, { view }) => boardCommands(board, view),
    menus: (board, { view }) => boardMenus(board, view),
  }),
  define({ id: "kinds", summary: "Node and link kinds: built-in, the graph's and the plugins'", needs: ["graph", "plugins", "actions"], start: ({ graph, plugins, actions }) => createKinds({ graph, plugins, actions }) }),
  define({ id: "presence", summary: "Who else is here: names, colours, cursors, selections", needs: ["client", "view"], start: ({ client, view }) => createPresence({ client, view }) }),
  define({
    id: "presets",
    summary: "Node templates in libraries: built-in, this computer's, yours and your team's",
    needs: ["client", "actions", "spaces", "accounts"],
    start: ({ client, actions, spaces, accounts }) => createPresets({ client, actions, spaces, accounts }),
    commands: (presets) => presetCommands(presets),
    menus: (presets) => presetMenus(presets),
    toolbar: (presets) => presetToolbar(presets),
  }),
  define({ id: "assets", summary: "Files kept with the work: the project's and this computer's", needs: ["client", "access"], start: ({ client, access }) => createAssets({ client, access }) }),

  // Reading code, plugins, AI.
  define({
    id: "analysis",
    summary: "Reading a project's code into the graph",
    needs: ["client", "graph", "actions", "platform"],
    start: ({ client, graph, actions, platform }) => createAnalysis({ client, graph, actions, readsFolders: platform.readsFolders, pickFolder: platform.pickFolder, directoryPicker: platform.directoryPicker }),
    menus: () => analysisMenus(),
    toolbar: () => analysisToolbar(),
  }),
  define({ id: "plugins", summary: "Installed plugins, their commands and notices", needs: ["client", "platform"], start: ({ client, platform }) => createPlugins({ client, readsFolders: platform.readsFolders, pickFolder: platform.pickFolder }), menus: (plugins) => pluginMenus(plugins) }),
  define({ id: "pluginViews", summary: "What plugin windows show", needs: ["client", "plugins"], start: ({ client, plugins }) => createPluginViews(client, plugins) }),
  define({ id: "ai", summary: "AI suggestions: enrich after a scan, notes, groups, review", needs: ["client", "actions", "graph"], start: ({ client, actions, graph }) => createAi(client, actions, () => graph.state.graph), menus: () => aiMenus(), toolbar: () => aiToolbar() }),

  // The screen around it.
  define({ id: "theme", summary: "Light, dark or system", needs: [], start: () => createTheme(), commands: (theme) => themeCommands(theme), menus: (theme) => themeMenus(theme) }),
  define({
    id: "wm",
    summary: "Windows and their layout",
    needs: ["config", "windows"],
    // The default layout is the config's, when it has one.
    start: ({ config, windows }) =>
      createWindowManager(windows.list, () => {
        const fromConfig = config.config()?.layout;
        return fromConfig ? layoutFromConfig(fromConfig) : windows.layout();
      }),
    commands: (wm) => windowCommands(wm),
    menus: (wm) => windowMenus(wm),
  }),
  define({ id: "fields", summary: "The data the environment worked in adds to nodes, and the choices offered", needs: ["graph", "environments"], start: ({ graph, environments }) => createFields({ graph: () => graph.state.graph, environment: environments.current }) }),
  define({
    id: "environments",
    summary: "Environments the plugins bring (Coding, Writing...): views and layout of each",
    needs: ["plugins", "graph", "view", "wm"],
    start: ({ plugins, graph, view, wm }) => {
      const environments = createEnvironments({ plugins, graph, view });
      followEnvironmentLayouts(environments, plugins, wm);
      return environments;
    },
    commands: (environments) => environmentCommands(environments),
    menus: (environments) => environmentMenus(environments),
  }),
  define({
    id: "nodeAdder",
    summary: "The Add node window: a node named, placed where asked",
    needs: ["actions", "view", "scene", "wm", "kinds", "graph", "environments"],
    start: ({ actions, view, scene, wm, kinds, graph, environments }) =>
      createNodeAdder({
        actions,
        view,
        scene,
        toDefine: kinds.toDefine,
        linkKind: (from, kind, wired) => {
          const source = graph.state.graph.nodes.find((n) => n.id === from);
          if (!source) return null;
          return wired ? kinds.wire(source.kind, kind, environments.current()?.play?.choices ?? []) : kinds.suggest(source.kind, kind);
        },
        show: (near) => floatNear(wm, "add-node", near, { width: 320, height: 400 }),
        hide: () => wm.close("add-node"),
      }),
    commands: (adder) => nodeAdderCommands(adder),
    menus: () => nodeAdderMenus(),
    toolbar: () => nodeAdderToolbar(),
  }),
  define({
    id: "documents",
    summary: "Writing's documents: files of the project, made from templates, merged when two people save",
    needs: ["client", "graph", "environments"],
    start: ({ client, graph, environments }) => createDocuments({ client, graph, environments }),
  }),
  define({
    id: "pages",
    summary: "Node pages: the one shown, [[mentions]] and backlinks",
    needs: ["graph", "view", "actions", "nodeAdder", "documents"],
    start: ({ graph, view, actions, nodeAdder, documents }) => createPages({ graph, view, actions, nodeAdder, documents }),
  }),
  define({
    id: "play",
    summary: "Playing the environment's graph, one passage at a time, and exporting it",
    needs: ["graph", "environments", "project", "documents"],
    start: ({ graph, environments, project, documents }) => createPlay({ graph, environments, documents, title: () => project.state()?.name ?? "" }),
  }),
  define({
    id: "prototype",
    summary: "UI/UX's prototype: the board's screens clicked through, and exported as a site",
    needs: ["graph", "assets", "board", "project", "wm", "view"],
    start: ({ graph, assets, board, project }) => createPrototype({ graph, assets, board, title: () => project.state()?.name ?? "" }),
    commands: (prototype, { board, wm }) => prototypeCommands(prototype, board, (id) => wm.open(id)),
    menus: (prototype, { board, view }) => prototypeMenus(prototype, board, view),
  }),
  define({ id: "menu", summary: "The right-click menu, and the menu bar's open menu", needs: [], start: () => createContextMenu() }),
  define({ id: "shell", summary: "The menu bar's menus and the toolbar, contributed by the others", needs: [], start: () => createShell() }),
  define({ id: "commands", summary: "Named commands for shortcuts and menus", needs: [], start: () => createCommands() }),
  define({
    id: "palette",
    summary: "The command palette: every action, window and node, searched (Ctrl+K)",
    needs: ["shell", "commands", "wm", "graph", "view", "config", "kinds"],
    start: ({ shell, commands, wm, graph, view, config, kinds }) =>
      createPalette({ shell, commands, wm, graph, view, binds: () => config.config()?.keybinds ?? DEFAULT_KEYBINDS, kindLabel: (kind) => kinds.ofNode({ kind }).label }),
    commands: (palette) => paletteCommands(palette),
    menus: () => paletteMenus(),
  }),
  define({ id: "inspector", summary: "Which inspector sections are folded, per environment", needs: ["environments"], start: ({ environments }) => createInspectorSections(() => environments.current()?.id ?? null) }),
] as const satisfies readonly Subsystem<Services, keyof Services, keyof Services>[];

/**
 * A compile-time check that every service of `App` has a subsystem: a missing one makes this
 * line fail to type-check, naming it.
 */
export const EVERY_SERVICE_STARTS: Exclude<keyof App, (typeof SUBSYSTEMS)[number]["id"]> extends never ? true : Exclude<keyof App, (typeof SUBSYSTEMS)[number]["id"]> = true;
